import React from 'react';
import PropTypes from 'prop-types';
import useGroupLabels from 'hooks/useGroupLabels';
import TeamColourDot from 'components/admin/TeamColourDot';
import { formatAgeViewGroup, formatGroupCell } from 'utils/groupLabel';
import { formatPoints } from 'utils/points';
import styles from './SeasonChampions.module.css';

// The Season Champions table (points slice 6, docs/school-carnivals-points-slice-6.md in the portal repo):
// each list and gender's 1st, 2nd and 3rd over the active season, as the server places them
// (SeasonChampionsDto.lists: equal totals share a place and the next place follows; a combined group
// ticked at every meet its ages took part in is one list). The Points page's group view look: a heading
// for each list and gender, girls pink and boys blue, the place badges, the group and gender badges.

const placeBadgeClass = (place) => {
  if (place === 1) return styles.placeGold;
  if (place === 2) return styles.placeSilver;
  return styles.placeBronze;
};

const SeasonChampions = ({ lists, teamWords }) => {
  // A list heading reads "U11 F", "Y6 F" or "11 F"; a combined list by its name and range, as Age View
  // heads one: "Year 11/12 (Y11-Y12) F".
  const { groupingBasis } = useGroupLabels();
  const columnCount = teamWords ? 7 : 6;

  return (
    <div className={styles.card}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.centered}>Place</th>
              <th>Name</th>
              <th aria-label="Group and gender" />
              {teamWords && <th>{teamWords.team}</th>}
              <th className={styles.centered}>Meets</th>
              <th className={styles.centered}>Events</th>
              <th className={styles.centered}>Total Points</th>
            </tr>
          </thead>
          <tbody>
            {lists.flatMap((list) => [
              <tr
                key={`list-${list.key}-${list.gender}`}
                className={`${styles.listHeader} ${list.gender === 'F' ? styles.listFemale : styles.listMale}`}
              >
                <td colSpan={columnCount}>
                  {formatAgeViewGroup(groupingBasis, list.key, list.name)} {list.gender}
                </td>
              </tr>,
              ...list.athletes.map((athlete) => (
                <tr key={`${list.key}-${list.gender}-${athlete.athleteProfileID}`}>
                  <td className={styles.centered}>
                    <span className={`${styles.placeBadge} ${placeBadgeClass(athlete.place)}`}>{athlete.place}</span>
                  </td>
                  <td className={styles.name}>
                    {athlete.firstName} {athlete.lastName}
                  </td>
                  <td>
                    <span className={styles.badgeCell}>
                      <span className={styles.groupBadge}>{formatGroupCell(groupingBasis, athlete.ageGroup)}</span>
                      <span
                        className={`${styles.genderBadge} ${athlete.gender === 'F' ? styles.genderFemale : styles.genderMale}`}
                      >
                        {athlete.gender}
                      </span>
                    </span>
                  </td>
                  {teamWords && (
                    <td>
                      {athlete.team && (
                        <span className={styles.houseInner}>
                          <TeamColourDot colour={athlete.team.colour} />
                          {athlete.team.name}
                        </span>
                      )}
                    </td>
                  )}
                  <td className={styles.centered}>{athlete.meetsParticipated}</td>
                  <td className={styles.centered}>{athlete.eventsParticipated}</td>
                  <td className={`${styles.centered} ${styles.total}`}>{formatPoints(athlete.totalPoints)}</td>
                </tr>
              )),
            ])}
          </tbody>
        </table>
      </div>
    </div>
  );
};

SeasonChampions.propTypes = {
  // The lists, youngest first, girls before boys (SeasonChampionsDto.lists).
  lists: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
      gender: PropTypes.string,
      athletes: PropTypes.arrayOf(
        PropTypes.shape({
          athleteProfileID: PropTypes.string,
          firstName: PropTypes.string,
          lastName: PropTypes.string,
          ageGroup: PropTypes.number,
          gender: PropTypes.string,
          team: PropTypes.shape({ name: PropTypes.string, colour: PropTypes.string }),
          place: PropTypes.number,
          meetsParticipated: PropTypes.number,
          eventsParticipated: PropTypes.number,
          totalPoints: PropTypes.number,
        })
      ),
    })
  ).isRequired,
  // A school's words for a house (utils/teamLabel.js); null for a club, which has no House column.
  teamWords: PropTypes.shape({ team: PropTypes.string }),
};

export default SeasonChampions;
